/**
 * Роли пользователя: порядок старшинства, подписи и производные правила (задача 22).
 *
 * Роль «Пользователь» (`user`) есть у любого аккаунта: экран заявки доступен каждому
 * вошедшему. Роли «Организатор» и «Администратор» назначаются экраном «Администратор»
 * (задача 23) или правкой справочника `reference-data/roles.json`; они добавляются к
 * базовой — по одному email может быть назначено несколько ролей сразу.
 *
 * Старшинство задано порядком в `USER_ROLES`: организатор — владелец справочников и
 * мероприятия, администратор — его помощник (решение 17.10.2026). Старшая роль набора
 * определяет домашний экран (`homeHref` в `src/frontend/router.ts`), порядок пунктов
 * меню аккаунта и порядок подписи ролей в мастхеде.
 */
import type { UserRole } from './types';

/** Роли по старшинству: первая в списке — старшая роль набора. Базовая — последняя. */
export const USER_ROLES: UserRole[] = ['organizer', 'administrator', 'user'];

/** Подписи ролей для интерфейса: подпись в мастхеде и пункты меню аккаунта. */
export const ROLE_TITLES: Record<UserRole, string> = {
  organizer: 'Организатор',
  administrator: 'Администратор',
  user: 'Пользователь',
};

/**
 * Подписи ролей в дательном падеже — для ответа 403 «Раздел доступен организатору»
 * (RBAC, `withRoles` в src/backend/auth/session.ts). Отдельная карта, а не `ROLE_TITLES`:
 * склейка «Раздел доступен » с именительным падежом читалась бы как ошибка.
 */
export const ROLE_DATIVE_TITLES: Record<UserRole, string> = {
  organizer: 'организатору',
  administrator: 'администратору',
  user: 'пользователю',
};

/**
 * Кому открыт раздел — подпись для отказа в доступе (403): по одной роли
 * «организатору сервиса», по нескольким — «организатору или администратору сервиса».
 */
export function allowedRolesText(roles: UserRole[]): string {
  return `${roles.map((role) => ROLE_DATIVE_TITLES[role]).join(' или ')} сервиса`;
}

/**
 * Роли, которые назначаются вручную (секция «Пользователи» экрана «Администратор», задача 23):
 * базовая «Пользователь» есть у любого аккаунта и в назначениях не участвует.
 * Один список на бэкенде (валидация запроса) и на фронтенде (чекбоксы таблицы).
 */
export const ASSIGNABLE_ROLES: UserRole[] = USER_ROLES.filter((role) => role !== 'user');

/**
 * Проверяет, что значение из внешнего источника (файл ролей, localStorage) — известная
 * роль: неизвестная роль не должна попадать ни в набор прав, ни в разметку.
 */
export function isUserRole(value: unknown): value is UserRole {
  return typeof value === 'string' && (USER_ROLES as string[]).includes(value);
}

/** Приводит набор ролей к порядку старшинства; дубли ролей отбрасываются. */
export function sortRoles(roles: UserRole[]): UserRole[] {
  return USER_ROLES.filter((role) => roles.includes(role));
}

/**
 * Набор ролей для интерфейса: у пустого или неизвестного набора — базовая роль
 * «Пользователь». Так рисуется меню аккаунта, если сессия записана прежней версией
 * сервиса или роли в ней не сохранены: пользователь остаётся на экране заявки.
 */
export function visibleRoles(roles: UserRole[]): UserRole[] {
  const sorted = sortRoles(roles);
  return sorted.length > 0 ? sorted : ['user'];
}

/**
 * Старшая роль набора — её экран показывается домашним. У пустого или неизвестного
 * набора старшая роль — базовая «Пользователь»: вход всегда ведёт хотя бы на экран заявки.
 */
export function primaryRole(roles: UserRole[]): UserRole {
  return sortRoles(roles)[0] ?? 'user';
}

/**
 * Подпись ролей для мастхеда: «Организатор», «Организатор, Администратор», «Пользователь».
 *
 * Базовая роль «Пользователь» в подпись не попадает, если у пользователя есть другие:
 * экран заявки доступен всем, и «Организатор, Пользователь» только удлинило бы подпись.
 */
export function roleLabel(roles: UserRole[]): string {
  const assigned = visibleRoles(roles).filter((role) => role !== 'user');
  const shownRoles: UserRole[] = assigned.length > 0 ? assigned : ['user'];
  return shownRoles.map((role) => ROLE_TITLES[role]).join(', ');
}
